import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MemberColor, MemberColorHint } from '@awwyeah/shared';
import { apiGetInvite, AuthApiError } from '../api';
import { AvatarPicker } from '../components/AvatarPicker';
import { ColorPicker } from '../components/ColorPicker';
import { useAuth } from '../context';
import { extractInviteToken } from '../invitePath';

export interface RegisterPageProps {
  /** Invite token. Falls back to parsing `/invite/:token` from the URL when omitted. */
  token?: string;
  redirectTo?: string;
}

type InviteCheck =
  | { status: 'checking' }
  | { status: 'valid'; members: MemberColorHint[] }
  | { status: 'invalid'; reason: string };

export function RegisterPage({
  token: tokenProp,
  redirectTo = '/',
}: RegisterPageProps): JSX.Element {
  const { register } = useAuth();
  const navigate = useNavigate();
  const token = tokenProp ?? extractInviteToken(window.location.pathname) ?? '';

  const [inviteCheck, setInviteCheck] = useState<InviteCheck>({ status: 'checking' });
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [color, setColor] = useState<MemberColor | null>(null);
  const [avatarEmoji, setAvatarEmoji] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setInviteCheck({ status: 'invalid', reason: 'not_found' });
      return;
    }
    let cancelled = false;
    apiGetInvite(token)
      .then((status) => {
        if (cancelled) return;
        setInviteCheck(
          status.valid
            ? { status: 'valid', members: status.members ?? [] }
            : { status: 'invalid', reason: status.reason ?? 'not_found' },
        );
      })
      .catch(() => {
        if (!cancelled) setInviteCheck({ status: 'invalid', reason: 'not_found' });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!color) {
      setError('Pick a colour.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await register({
        inviteToken: token,
        name,
        email,
        password,
        color,
        avatarEmoji: avatarEmoji || '👤',
      });
      void navigate(redirectTo, { replace: true });
    } catch (err) {
      if (err instanceof AuthApiError && err.code === 'email_taken') {
        setError('That email is already registered — try signing in instead.');
      } else if (err instanceof AuthApiError && err.code === 'invalid_invite') {
        setError('This invite link is no longer valid.');
      } else {
        setError('Something went wrong. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (inviteCheck.status === 'checking') {
    return <p className="p-6 text-fg-3">Checking invite…</p>;
  }

  if (inviteCheck.status === 'invalid') {
    const message =
      inviteCheck.reason === 'used'
        ? 'This invite has already been used.'
        : inviteCheck.reason === 'expired'
          ? 'This invite has expired.'
          : "This invite link isn't valid.";
    return (
      <div className="flex min-h-dvh items-center justify-center bg-bg px-4">
        <p className="max-w-sm text-center text-fg-2">
          {message} Ask another household member for a new link.
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg px-4 py-10">
      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="w-full max-w-sm space-y-4 rounded-lg border border-line bg-surface p-6 shadow-sm lg:max-w-md lg:p-8"
      >
        <h1 className="font-display text-xl tracking-tight text-fg">Join the household</h1>

        <label className="block text-sm">
          <span className="mb-1 block text-fg-2">Name</span>
          <input
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded border border-line bg-bg px-3 py-2 text-fg"
          />
        </label>

        <label className="block text-sm">
          <span className="mb-1 block text-fg-2">Email</span>
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded border border-line bg-bg px-3 py-2 text-fg"
          />
        </label>

        <label className="block text-sm">
          <span className="mb-1 block text-fg-2">Password</span>
          <input
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded border border-line bg-bg px-3 py-2 text-fg"
          />
        </label>

        <div className="text-sm">
          <span className="mb-1 block text-fg-2">Colour</span>
          <ColorPicker
            value={color}
            onChange={setColor}
            takenBy={inviteCheck.status === 'valid' ? inviteCheck.members : []}
          />
        </div>

        <div className="text-sm">
          <span className="mb-1 block text-fg-2">Avatar</span>
          <AvatarPicker value={avatarEmoji} onChange={setAvatarEmoji} />
        </div>

        {error && <p className="text-sm text-danger">{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded bg-inverse px-3 py-2 font-medium text-inverse-fg disabled:opacity-50"
        >
          {submitting ? 'Creating account…' : 'Create account'}
        </button>
      </form>
    </div>
  );
}
